/** 支持 YYYY-MM-DD、YYYY/MM/DD，月日可为 1～2 位（如 2026/1/23） */
const DATE_REG = /^\d{4}[-\/]\d{1,2}[-\/]\d{1,2}$/;

function normalizeDateStr(s: string): string {
  const parts = s.split(/[-\/]/);
  if (parts.length !== 3) return s;
  const [y, m, d] = parts;
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}
const MAX_DECIMALS = 2;

export interface ParsedDetailRow {
  businessDate: string;
  adjustmentAmount: number;
  amountInCents: number;
  _error?: string;
}

/** 调账明细 CSV 模板内容（表头：日期,金额） */
export const DETAIL_ADJUSTMENT_TEMPLATE_CSV = '日期,金额\n';

/** 下载调账明细模板 CSV */
export function downloadDetailAdjustmentTemplate(): void {
  const blob = new Blob(['\uFEFF' + DETAIL_ADJUSTMENT_TEMPLATE_CSV], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = '调账明细模板.csv';
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * 解析 CSV 文件为调账明细行
 * 要求：首行为表头，列名为「日期」「金额」；日期 YYYY-MM-DD 或 YYYY/MM/DD（月日可为 1～2 位，如 2026/1/23），金额为数值（最多 2 位小数，可正可负）
 */
export function parseCsvFile(file: File): Promise<ParsedDetailRow[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result;
        if (typeof text !== 'string') {
          reject(new Error('无法读取文件'));
          return;
        }
        const lines = text.split(/\r?\n/).filter((line) => line.trim());
        if (lines.length < 2) {
          reject(new Error('CSV 至少需要表头行和一行数据，列名为：日期, 金额'));
          return;
        }
        const headerLine = lines[0];
        const headers = parseCsvLine(headerLine);
        const dateIdx = headers.findIndex((h) => h.trim() === '日期');
        const amountIdx = headers.findIndex((h) => h.trim() === '金额');
        if (dateIdx === -1 || amountIdx === -1) {
          reject(new Error('表头必须包含列名：日期, 金额'));
          return;
        }
        const rows: ParsedDetailRow[] = [];
        for (let i = 1; i < lines.length; i++) {
          const cells = parseCsvLine(lines[i]);
          const dateStr = (cells[dateIdx] ?? '').trim();
          const rawAmount = (cells[amountIdx] ?? '').trim();
          if (!dateStr) continue;
          let amountNum = 0;
          if (rawAmount !== '') {
            amountNum = Number(rawAmount);
            if (Number.isNaN(amountNum)) {
              rows.push({
                businessDate: dateStr,
                adjustmentAmount: 0,
                amountInCents: 0,
                _error: '金额必须为数值'
              });
              continue;
            }
            const decimals = (rawAmount.split('.')[1] || '').length;
            if (decimals > MAX_DECIMALS) {
              rows.push({
                businessDate: dateStr,
                adjustmentAmount: amountNum,
                amountInCents: Math.round(amountNum * 100),
                _error: `金额最多${MAX_DECIMALS}位小数`
              });
              continue;
            }
          }
          const amountInCents = Math.round(amountNum * 100);
          rows.push({
            businessDate: dateStr,
            adjustmentAmount: amountNum,
            amountInCents
          });
        }
        resolve(rows);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('读取文件失败'));
    reader.readAsText(file, 'UTF-8');
  });
}

/** 解析单行 CSV（简单实现：按逗号分割，不考虑引号内逗号） */
function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      inQuotes = !inQuotes;
    } else if (inQuotes) {
      current += c;
    } else if (c === ',') {
      result.push(current);
      current = '';
    } else {
      current += c;
    }
  }
  result.push(current);
  return result;
}

export function validateDetailRows(
  rows: ParsedDetailRow[],
  periodStart: string,
  periodEnd: string
): { valid: ParsedDetailRow[]; errors: string[] } {
  const errors: string[] = [];
  const seen = new Set<string>();
  const valid: ParsedDetailRow[] = [];
  const periodStartDate = normalizeDateStr(periodStart.slice(0, 10));
  const periodEndDate = normalizeDateStr(periodEnd.slice(0, 10));
  for (const row of rows) {
    if (row._error) {
      errors.push(`日期 ${row.businessDate}: ${row._error}`);
      continue;
    }
    if (!DATE_REG.test(row.businessDate)) {
      errors.push(`日期格式必须为 YYYY-MM-DD 或 YYYY/MM/DD（月日可为 1～2 位）: ${row.businessDate}`);
      continue;
    }
    const normalizedDate = normalizeDateStr(row.businessDate);
    if (seen.has(normalizedDate)) {
      errors.push(`业务发生日期不可重复: ${row.businessDate}`);
      continue;
    }
    seen.add(normalizedDate);
    if (normalizedDate < periodStartDate || normalizedDate > periodEndDate) {
      errors.push(`业务日期 ${row.businessDate} 不在当前账期范围内（${periodStartDate} ~ ${periodEndDate}）`);
      continue;
    }
    valid.push({ ...row, businessDate: normalizedDate });
  }
  return { valid, errors };
}

export function downloadDspAdjustBillTemplate(): void {
  const BOM = "﻿";
  const csv = `${BOM}业务单号,基础线路计费\n`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "DSP明细调账模板.csv";
  a.click();
  URL.revokeObjectURL(url);
}
